import React from 'react';

import { Avatar, Box, rem, useColorModeValue } from '@bonusly/design-system';

interface PulsingAvatarCirclesProps {
	avatarUrl?: string;
	avatarName?: string;
	isAnimating?: boolean;
	size?: 'sm' | 'md' | 'lg' | 'xl';
	/** Use solid thick rings instead of thin circles (for feed player) */
	solidRings?: boolean;
	/** Ring color for solid rings mode */
	ringColor?: string;
	pulseColor?: string;
	staticColor?: string;
	/** Audio level from 0-1 to drive pulse intensity (optional, uses CSS animation if not provided) */
	audioLevel?: number;
}

// Sizes for thin circle mode (recording modal)
const SIZE_MAP = {
	sm: { avatar: 48, circles: [72, 96, 120, 144] },
	md: { avatar: 64, circles: [96, 128, 160, 192] },
	lg: { avatar: 96, circles: [140, 184, 228, 272] },
	xl: { avatar: 120, circles: [176, 232, 288, 344] },
};

// Sizes for solid ring mode (feed player) - thick filled bands with no gaps
// Avatar has a 2px white border, so visual avatar size = avatar + 4px
// Bands overlap by 2px to prevent subpixel rendering gaps
// Using border: the box size IS the outer diameter, border draws inward
const SOLID_RING_SIZE_MAP = {
	sm: {
		avatar: 64,
		avatarBorder: 2,
		container: 190,
		// Avatar visual = 64 + 4 = 68px
		// Band width ~21px, overlapping by 2px
		bands: [
			{ outer: 190, inner: 148 }, // outermost
			{ outer: 150, inner: 108 }, // middle (overlaps outer by 2px)
			{ outer: 110, inner: 68 }, // innermost (overlaps middle by 2px)
		],
	},
	md: {
		avatar: 96,
		avatarBorder: 2,
		container: 224,
		// Avatar visual = 96 + 4 = 100px
		bands: [
			{ outer: 224, inner: 180 },
			{ outer: 182, inner: 140 },
			{ outer: 142, inner: 100 },
		],
	},
	lg: {
		avatar: 128,
		avatarBorder: 2,
		container: 280,
		// Avatar visual = 128 + 4 = 132px
		bands: [
			{ outer: 280, inner: 230 },
			{ outer: 232, inner: 182 },
			{ outer: 184, inner: 132 },
		],
	},
	xl: {
		avatar: 160,
		avatarBorder: 2,
		container: 350,
		// Avatar visual = 160 + 4 = 164px
		bands: [
			{ outer: 350, inner: 288 },
			{ outer: 290, inner: 228 },
			{ outer: 230, inner: 164 },
		],
	},
};

export const PulsingAvatarCircles: React.FC<PulsingAvatarCirclesProps> = ({
	avatarUrl,
	avatarName,
	isAnimating = false,
	size = 'lg',
	solidRings = false,
	ringColor,
	pulseColor,
	staticColor,
	audioLevel,
}) => {
	// Use gray colors by default for consistency across recording modal and player
	const defaultPulseColor = useColorModeValue('gray.800', 'white');
	const defaultStaticColor = useColorModeValue('gray.400', 'gray.500');
	// Default ring color for solid rings mode (light blue)
	const defaultSolidRingColor = useColorModeValue('#9ECEF0', '#4A90A4');
	const circleColor = pulseColor || defaultPulseColor;
	const staticCircleColor = staticColor || defaultStaticColor;

	// If audioLevel is provided, use it to drive the pulse scale
	// Otherwise fall back to isAnimating for CSS animation
	const hasAudioLevel = audioLevel !== undefined && isAnimating;

	// Calculate scale based on audio level (0-1)
	// Each circle gets a slightly different scale multiplier for a ripple effect
	const getScale = (circleIndex: number) => {
		if (!hasAudioLevel) return 1;
		// Inner circles pulse more, outer circles pulse less
		const baseScale = 1;
		const maxExtraScale = 0.15 - circleIndex * 0.03; // Inner: 0.15, outer: 0.06
		return baseScale + audioLevel * maxExtraScale;
	};

	// Calculate opacity based on audio level
	const getOpacity = (baseOpacity: number, _circleIndex: number) => {
		if (!hasAudioLevel) return isAnimating ? baseOpacity : baseOpacity - 0.2;
		// Fade in/out based on audio level
		const minOpacity = baseOpacity - 0.3;
		return minOpacity + audioLevel * 0.5;
	};

	// Solid rings mode (for feed player - matches Figma design with thick filled bands)
	if (solidRings) {
		const ringSizes = SOLID_RING_SIZE_MAP[size];
		const finalRingColor = ringColor || defaultSolidRingColor;

		// Calculate band widths from outer/inner sizes
		const bands = ringSizes.bands.map((band) => ({
			size: band.outer,
			width: (band.outer - band.inner) / 2,
		}));

		// Audio-reactive opacity: rings fade in based on audio level
		// When loud: innermost ring should be fully visible (dark gradient right next to avatar)
		// When quiet: rings fade out progressively from outer to inner
		const level = audioLevel || 0;
		const getAudioOpacity = (ringIndex: number) => {
			// Base opacities when not playing - subtle gradient visible
			const baseOpacities = [0.2, 0.35, 0.5]; // outer, middle, inner

			if (!isAnimating) {
				return baseOpacities[ringIndex];
			}

			// When playing: rings respond to audio level
			// ringIndex 0 = outermost, 2 = innermost
			// Key: innermost ring should be fully opaque when loud
			if (ringIndex === 2) {
				// Inner ring: minimum visibility when quiet, fully opaque when loud
				// This ensures the dark gradient is visible right next to the avatar's white border
				return Math.min(1.0, 0.4 + level * 0.6);
			} else if (ringIndex === 1) {
				// Middle ring: fades in as audio gets louder
				return Math.min(0.9, 0.25 + level * 0.65);
			} else {
				// Outer ring: most subtle, only prominent at high levels
				return Math.min(0.8, 0.15 + level * 0.65);
			}
		};

		return (
			<Box position="relative" display="flex" alignItems="center" justifyContent="center" width={rem(ringSizes.container)} height={rem(ringSizes.container)}>
				{/* Outermost band - appears at high audio levels */}
				<Box
					position="absolute"
					width={rem(bands[0].size)}
					height={rem(bands[0].size)}
					borderRadius="full"
					border={`${bands[0].width}px solid`}
					borderColor={finalRingColor}
					opacity={getAudioOpacity(0)}
					transform={`scale(${getScale(2)})`}
					transition="transform 0.1s ease-out, opacity 0.1s ease-out"
				/>

				{/* Middle band - appears at medium audio levels */}
				<Box
					position="absolute"
					width={rem(bands[1].size)}
					height={rem(bands[1].size)}
					borderRadius="full"
					border={`${bands[1].width}px solid`}
					borderColor={finalRingColor}
					opacity={getAudioOpacity(1)}
					transform={`scale(${getScale(1)})`}
					transition="transform 0.1s ease-out, opacity 0.1s ease-out"
				/>

				{/* Inner band - appears first at low audio levels */}
				<Box
					position="absolute"
					width={rem(bands[2].size)}
					height={rem(bands[2].size)}
					borderRadius="full"
					border={`${bands[2].width}px solid`}
					borderColor={finalRingColor}
					opacity={getAudioOpacity(2)}
					transform={`scale(${getScale(0)})`}
					transition="transform 0.1s ease-out, opacity 0.1s ease-out"
				/>

				{/* Avatar with white border - centered absolutely */}
				<Box
					position="absolute"
					borderRadius="full"
					border="2px solid white"
					boxShadow="0 1px 4px rgba(0,0,0,0.08)"
					display="flex"
					alignItems="center"
					justifyContent="center"
				>
					<Avatar src={avatarUrl} name={avatarName || 'User'} size="2xl" width={rem(ringSizes.avatar)} height={rem(ringSizes.avatar)} />
				</Box>
			</Box>
		);
	}

	// Thin circles mode (for recording modal)
	const sizes = SIZE_MAP[size];

	return (
		<Box position="relative" display="flex" alignItems="center" justifyContent="center" width={rem(sizes.circles[3])} height={rem(sizes.circles[3])}>
			{/* Outermost circle */}
			<Box
				position="absolute"
				width={rem(sizes.circles[3])}
				height={rem(sizes.circles[3])}
				borderRadius="full"
				border="3px solid"
				borderColor={isAnimating ? circleColor : staticCircleColor}
				opacity={getOpacity(0.4, 3)}
				transform={`scale(${getScale(3)})`}
				transition={hasAudioLevel ? 'transform 0.1s ease-out, opacity 0.1s ease-out' : undefined}
			/>

			{/* Third circle */}
			<Box
				position="absolute"
				width={rem(sizes.circles[2])}
				height={rem(sizes.circles[2])}
				borderRadius="full"
				border="3px solid"
				borderColor={isAnimating ? circleColor : staticCircleColor}
				opacity={getOpacity(0.5, 2)}
				transform={`scale(${getScale(2)})`}
				transition={hasAudioLevel ? 'transform 0.1s ease-out, opacity 0.1s ease-out' : undefined}
			/>

			{/* Second circle */}
			<Box
				position="absolute"
				width={rem(sizes.circles[1])}
				height={rem(sizes.circles[1])}
				borderRadius="full"
				border="3px solid"
				borderColor={isAnimating ? circleColor : staticCircleColor}
				opacity={getOpacity(0.7, 1)}
				transform={`scale(${getScale(1)})`}
				transition={hasAudioLevel ? 'transform 0.1s ease-out, opacity 0.1s ease-out' : undefined}
			/>

			{/* Inner circle */}
			<Box
				position="absolute"
				width={rem(sizes.circles[0])}
				height={rem(sizes.circles[0])}
				borderRadius="full"
				border="4px solid"
				borderColor={isAnimating ? circleColor : staticCircleColor}
				opacity={getOpacity(0.9, 0)}
				transform={`scale(${getScale(0)})`}
				transition={hasAudioLevel ? 'transform 0.1s ease-out, opacity 0.1s ease-out' : undefined}
			/>

			{/* Avatar */}
			<Avatar src={avatarUrl} name={avatarName || 'User'} size="xl" width={rem(sizes.avatar)} height={rem(sizes.avatar)} />
		</Box>
	);
};
